import { useState, useEffect } from "react";
import { ResultListFrmDto, Gender } from "./api/Dtos";
import { DELETE, PUT, POST, GET } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import { HStack, VStack, Button, Spacer, Tooltip } from "@chakra-ui/react";
import { FastTable } from "./Table/FastTable";
import { useResultListFrmFromBackend } from "./Services/ResultListFrmService";
import AddResultRptListModal from "./Modals/AddResultListFrmModal";

const modifyFields = [
  "name",
  "disciplineName",
  "displayOrder",
  "gender",
  "showResults",
  "earliestBirthYear",
  "latestBirthYear",
];

const DISPLAY_NAME_NAME = "Namn";
const DISPLAY_NAME_DISCIPLINE_NAME = "Klass";
const DISPLAY_NAME_DISPLAY_ORDER = "Sortering";
const DISPLAY_NAME_GENDER = "Kön";
const DISPLAY_NAME_SHOW_RESULTS = "Visa resultat";
const DISPLAY_NAME_EARLIEST_BIRTH_YEAR = "Född tidigast";
const DISPLAY_NAME_LATEST_BIRTH_YEAR = "Född senast";

const modifyDisplayLabels = [
  DISPLAY_NAME_NAME,  
  DISPLAY_NAME_DISCIPLINE_NAME,
  DISPLAY_NAME_DISPLAY_ORDER,
  DISPLAY_NAME_GENDER,
  DISPLAY_NAME_SHOW_RESULTS,
  DISPLAY_NAME_EARLIEST_BIRTH_YEAR,
  DISPLAY_NAME_LATEST_BIRTH_YEAR,
];

export function ResultRptLists() {
  const { resultListFrm, setResultListFrm } = useResultListFrmFromBackend();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const globalContext = useGlobalContext();
  const [showConfirmDeleteResultRptList, setShowConfirmDeleteResultRptList] = useState(false);

  // Get competitions from backend
  useEffect(() => {
    console.log(
      "ResultRptLists component rendered with resultRptLists:",
      resultListFrm
    );
    if (loading) {
        setLoading(false);
    }
    setError(
      globalContext.competition?.id !== undefined
        ? ""
        : "Ingen tävling är vald."
    );
  }, [resultListFrm, globalContext.competition]);


  if (loading) return <div>Loading...</div>;
  if (error.length > 0) return <div>Fel: {error}</div>;

  const addResultRptList = async (newResultRptList: ResultListFrmDto) => {
    console.info(
      "add: " +
        newResultRptList.name +
        ", id: " +
        newResultRptList.id.toString()
    );
    if (globalContext.competition?.tag !== undefined) {
      const success = await POST.resultRptList(newResultRptList);
      if (success) {
        console.log("Added resultRptList:", newResultRptList);
        setResultListFrm((old) => [...old, newResultRptList]);
      }
    } else {
      console.error("Ingen tävling är vald.");
    }
  };

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        {
          <VStack>
            <HStack>
              <Tooltip label={"Lägg till och definiera varje resultatlista."}>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  colorScheme="green"
                  padding={"40px"}
                  fontSize={"24px"}
                >
                  Lägg till en ny resultatlista
                </Button>
              </Tooltip>
            </HStack>
            <AddResultRptListModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onAddRow={addResultRptList}
            />
          </VStack>
        }
      </HStack>

      <FastTable
        data={resultListFrm}
        columnWidths={[28, 17, 10, 12, 12, 12, 12]}
        pageHeader={`Resultatlistor till ${globalContext.competition?.tag ?? "Ingen tävling vald"}`}
        repeatHeaderRows={3}
        downloadFileName="Resultatlistor"
        headers={[
          DISPLAY_NAME_NAME,
          DISPLAY_NAME_DISCIPLINE_NAME,
          DISPLAY_NAME_DISPLAY_ORDER,
          DISPLAY_NAME_GENDER,
          DISPLAY_NAME_SHOW_RESULTS,
          DISPLAY_NAME_EARLIEST_BIRTH_YEAR,
          DISPLAY_NAME_LATEST_BIRTH_YEAR,
        ]}

        forceDisplayToTypeMap={
          new Map([
            [DISPLAY_NAME_NAME, "string"], 
            [DISPLAY_NAME_DISCIPLINE_NAME, "string"],
            [DISPLAY_NAME_DISPLAY_ORDER, "number"],
            [DISPLAY_NAME_GENDER, "string | null"],
            [DISPLAY_NAME_SHOW_RESULTS, "boolean"],
            [DISPLAY_NAME_EARLIEST_BIRTH_YEAR, "number"],
            [DISPLAY_NAME_LATEST_BIRTH_YEAR, "number"],
          ])
        }
        rowFormat={"2fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"}
        toRowData={(p) => {
          return [
            p.name,
            p.disciplineName,
            p.displayOrder,
            p.gender,
            p.showResults,
            p.earliestBirthYear,
            p.latestBirthYear,
          ];
        }}
        modifiableFields={modifyFields as Array<keyof ResultListFrmDto>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        onChange={async (updatedResultListFrmDto, oldResultListFrmDto) => {
          if (
            globalContext.competition?.id !== undefined &&
            globalContext.competition?.tag !== undefined &&
            updatedResultListFrmDto.name != null &&
            updatedResultListFrmDto.disciplineName.length > 0 
          ) {
            if (updatedResultListFrmDto.gender?.substring(0, 4) === "null") {
              updatedResultListFrmDto.gender = null;
            }
            const success = await PUT.resultRptList(
              updatedResultListFrmDto,
              globalContext.competition.tag
            );
            if (success) {
              setResultListFrm((old) => {
                return old.map((p) =>
                  p.id === updatedResultListFrmDto.id ? updatedResultListFrmDto : p
                );
              });
            }
            return success;
          } else {
            console.error("Ingen tävling är vald.");
          }
          return false;
        }}
        onDelete={async (deletedResultListFrmDto) => {
          const resultList =
            deletedResultListFrmDto;
          console.info(
            "Delete: " +
              resultList.id +
              ", name: " +
              resultList.name
          );
          const success = await DELETE.resultRptList(resultList);
          if (success) {
            setResultListFrm((old) => {
              return old.filter((p) => p.id != resultList.id);
            });
          }
          return true; 
        }}
      />
    </div>
  );
}

export default ResultRptLists;